import React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  SelectProps as MUISelectProps,
  SelectChangeEvent,
} from '@mui/material';
import { createAllOption } from '@common/types/constants';
import { Option } from '@common/types';
import { pick } from 'lodash';

interface CustomSelectProps extends Omit<MUISelectProps<string>, 'onChange'> {
  name: string;
  label?: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  clearable?: boolean;
  clearLabel?: string;
  record_id?: string;
  options: Option[];
  error?: boolean;
  value?: string;
  helperText?: string;
}

export const Select = React.forwardRef<HTMLDivElement, CustomSelectProps>(
  (
    {
      name,
      label,
      onChange,
      error = false,
      options = [],
      value,
      clearable,
      clearLabel = '-',
      helperText,
      sx,
      ...props
    },
    ref,
  ) => {
    const _options = [
      ...(clearable ? [createAllOption(clearLabel)] : []),
      ...options,
    ];

    return (
      <FormControl
        sx={{
          minWidth: '140px',
          width: '100%',
          ...sx,
        }}
        {...pick(props, 'size', 'required', 'variant')}
      >
        <InputLabel shrink={!!value || value === ''} error={error}>
          {label}
        </InputLabel>
        <MUISelect
          inputRef={ref}
          ref={ref}
          value={value}
          name={name}
          label={label}
          variant='outlined'
          error={error}
          fullWidth
          onChange={onChange}
          inputProps={{
            notched: 'true',
          }}
          displayEmpty
          renderValue={selected => {
            const found = _options.find(o => o.value === selected);
            const text = found?.label ?? '';
            return (
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </span>
            );
          }}
          {...props}
        >
          {_options.map((option, index) => (
            <MenuItem
              key={`${name}-${option.value}-${index}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </MUISelect>
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  },
);

Select.displayName = 'Select';
